import {
  type Accessor,
  createEffect,
  createMemo,
  createSignal,
  createUniqueId,
  lazy,
  on,
  Show,
  untrack,
} from "solid-js";
import { useTranslation } from "../../state/i18n";
import { ErrorMessage } from "../inputs/Input";
import InlineMessage from "../inlineMessage/InlineMessage";
import { Document } from "@tiptap/extension-document";
import { Paragraph } from "@tiptap/extension-paragraph";
import { Text } from "@tiptap/extension-text";
import { Bold } from "@tiptap/extension-bold";
import { Italic } from "@tiptap/extension-italic";
import { Underline } from "@tiptap/extension-underline";
import { Strike } from "@tiptap/extension-strike";
import { HardBreak } from "@tiptap/extension-hard-break";
import { History } from "@tiptap/extension-history";
import { TextAlign } from "@tiptap/extension-text-align";
import { ListItem, BulletList, OrderedList } from "@tiptap/extension-list";
import { HorizontalRule } from "@tiptap/extension-horizontal-rule";
import { TextStyle, BackgroundColor, LineHeight } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import { FontFamily } from "@tiptap/extension-font-family";
import { Blockquote } from "@tiptap/extension-blockquote";
import { CodeBlock } from "@tiptap/extension-code-block";
import { Heading } from "@tiptap/extension-heading";
import { Table, TableRow, TableCell, TableHeader } from "@tiptap/extension-table";
import { Gapcursor } from "@tiptap/extension-gapcursor";
import { Link } from "@tiptap/extension-link";
import StyleNode from "./tiptap/StyleNodeExtension";
import Indent from "./tiptap/IndentExtension";
import HighlightSelection from "./tiptap/HighlightSelectionExtension";
import { useWysiwyg, WysiwygProvider } from "./WysiwygContext";
import {
  BoldCommand,
  BulletListCommand,
  CodeCommand,
  FontFamilyCommand,
  HorizontalLineCommand,
  ImageCommand,
  IndentCommand,
  ItalicCommand,
  LineHeightCommand,
  LinkCommand,
  OrderedListCommand,
  OutdentCommand,
  PlaceholderCommand,
  StrikeThroughCommand,
  TableCommand,
  TextAlignCenterCommand,
  TextAlignLeftCommand,
  TextAlignRightCommand,
  TextBackgroundColorCommand,
  TextColorCommand,
  TextJustifyFullCommand,
  TextTypeCommand,
  UnderlineCommand,
} from "./commands";
import { initPlaceholder } from "../utils/placeholder/placeholder";
import { replacePillWithPlaceholder, toTipTapContent } from "../../utils/placeholderUtil";
import type { EditorView } from "@tiptap/pm/view";
import { GlobalHtmlAttributes, htmlAttribute } from "./tiptap/extensionUtil";
import withErrorBoundary from "../../utils/withErrorBoundary";
import type { Delimiter, StringTemplate } from "../../utils/stringTemplateUtil";
import { prependStyles, requiresCodeMode, splitStyleAndContent } from "../../utils/wysiwygUtil";
import { createEditor } from "./tiptap/solid/createEditor";
import { EditorContent } from "./tiptap/solid/EditorContent";
import type { DynamicValueId, SelectOptions } from "../../types/Menu";
import { isNil } from "lodash-es";
import { Extension } from "@tiptap/core";
import SeparatorLine from "../SeparatorLine";
import { CustomImage } from "./tiptap/ImageExtension";

type WysiwygProps = {
  ariaLabel: string;
  // Only read on mount: the editor owns its content, like TipTap and CodeMirror. Remount (key) to replace it
  initialContent: StringTemplate;
  parse: (value: string) => StringTemplate;
  onChange: (value: StringTemplate) => void;
  errorMessage?: string;
  placeholderMenuOptions: SelectOptions<DynamicValueId>;
  placeholderDelimiter?: Delimiter;
};

const globalHtmlAttributes = Extension.create({
  name: "htmlAttributes",
  addGlobalAttributes: () => [
    {
      types: ["image", "table", "tableCell", "tableHeader", "tableRow"],
      attributes: {
        width: htmlAttribute("width"),
        height: htmlAttribute("height"),
        margin: htmlAttribute("margin"),
        align: htmlAttribute("align"),
      },
    },
  ],
});

const CodeMirror = lazy(() => import("../codeMirror/CodeMirror"));

const Wysiwyg = (props: WysiwygProps) => {
  const { t } = useTranslation();
  const delimiter: Accessor<Delimiter> = createMemo(() => props.placeholderDelimiter ?? "${");
  const options = createMemo(() => props.placeholderMenuOptions);
  const placeholder = createMemo(() => initPlaceholder(() => editor, delimiter, options, "inline"));

  const [content, setContent] = createSignal(untrack(() => props.initialContent));
  const save = (value: StringTemplate) => {
    setContent(() => value);
    props.onChange(value);
  };

  const initialContent = toTipTapContent(
    untrack(content),
    untrack(() => placeholder().options()),
    t,
  );
  // Derived from the content rather than kept as typed, so a changed placeholder delimiter is applied as well
  const code = createMemo(() =>
    replacePillWithPlaceholder(toTipTapContent(content(), placeholder().options(), t), delimiter()),
  );
  const [codeOnly, setCodeOnly] = createSignal(requiresCodeMode(untrack(code)));
  const { html, styles: initialStyles } = splitStyleAndContent(initialContent);
  const [styles, setStyles] = createSignal(initialStyles);

  const editor = createEditor({
    extensions: [
      GlobalHtmlAttributes,
      globalHtmlAttributes,
      BackgroundColor,
      Blockquote.extend({
        content: "paragraph*", // Do not allow headings or other nodes in blockquotes
        priority: 101, // Higher priority than 'Bold' so keyboard shortcuts work
      }),
      Bold,
      CodeBlock,
      Color,
      Document,
      FontFamily,
      Gapcursor,
      HardBreak,
      Heading,
      HorizontalRule,
      History,
      CustomImage,
      Indent.configure({
        types: ["paragraph", "heading", "codeblock", "bulletList", "orderedList"],
      }),
      Italic,
      Link.configure({
        openOnClick: false,
        shouldAutoLink: (url) => url.startsWith("https://"),
      }),
      Paragraph,
      Strike,
      StyleNode,
      Table.configure({
        cellMinWidth: 0,
      }),
      HighlightSelection,
      TableRow,
      TableCell,
      TableHeader,
      Text,
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
      TextStyle,
      Underline,
      LineHeight,
      ListItem,
      BulletList,
      OrderedList,
      ...untrack(placeholder).extensions,
    ],
    editorProps: {
      transformPastedText: (text: string, _plain: boolean, view: EditorView) => {
        view.pasteHTML(
          toTipTapContent(
            props.parse(text),
            untrack(() => placeholder().options()),
            t,
          ),
        );
        return "";
      },
      transformPastedHTML: (value: string) =>
        toTipTapContent(
          props.parse(
            replacePillWithPlaceholder(
              value,
              untrack(() => delimiter()),
            ),
          ),
          untrack(() => placeholder().options()),
          t,
        ),
      attributes: {
        // Styling is done through CSS in `index.css` (scoped with the 'wysiwyg' class) to avoid adding classes to the
        // HTML data structure that we currently send to the backend.
        class: "wysiwyg h-full outline-none",
        role: "textbox",
        "aria-label": untrack(() => props.ariaLabel),
      },
    },
    content: html,
    onBlur: ({ editor }) => {
      if (codeOnly()) {
        return;
      }
      save(props.parse(prependStyles(styles(), replacePillWithPlaceholder(editor.getHTML(), delimiter()))));
    },
  });

  createEffect(
    on(
      () => placeholder().options(),
      () => {
        // TipTap would parse the content lossy, and the code only editor derives its code from the content itself
        if (untrack(codeOnly)) {
          return;
        }
        const current = untrack(content);
        const { html } = splitStyleAndContent(toTipTapContent(current, placeholder().options(), t));
        editor()?.commands.setContent(html);
      },
    ),
  );

  return (
    <Show when={!isNil(editor()) && editor} fallback={t(($) => $.WYSIWYG_LOADING)} keyed>
      {(initializedEditor) => (
        <WysiwygProvider
          // @ts-expect-error Necessary since the evaluation type of the Show still has Accessor<null> in its type
          value={{ editor: initializedEditor, addPlaceholder: placeholder().addPlaceholder, codeOnly }}
        >
          <div class="flex flex-col gap-1">
            <Editor
              options={placeholder().options()}
              delimiter={delimiter()}
              onChange={save}
              errorMessage={props.errorMessage}
              parse={props.parse}
              styles={styles()}
              setStyles={setStyles}
              setCodeOnly={setCodeOnly}
              code={code()}
            />
            <Show when={codeOnly()}>
              <InlineMessage class="mt-1" variant="warning" label={t(($) => $.WYSIWYG_CODE_ONLY_DESCRIPTION)} />
            </Show>
            <ErrorMessage errorMessage={props.errorMessage} />
          </div>
        </WysiwygProvider>
      )}
    </Show>
  );
};

const Editor = <T,>(
  props: {
    options: SelectOptions<T>;
    delimiter: Delimiter;
    styles: string;
    setStyles: (styles: string) => void;
    setCodeOnly: (codeOnly: boolean) => void;
    code: string;
  } & Pick<WysiwygProps, "onChange" | "errorMessage" | "parse">,
) => {
  const { t } = useTranslation();
  const { editor, mode, codeOnly } = useWysiwyg();

  const uniqueId = createUniqueId();
  return (
    <div
      role="group"
      class="rounded-sm border bg-surface-default"
      classList={{
        "border-default": isNil(props.errorMessage),
        "border-error": !isNil(props.errorMessage),
      }}
    >
      <Toolbar />
      <div class="flex max-h-140 min-h-71 resize-y overflow-y-auto rounded-b-sm border-t border-default" id={uniqueId}>
        <Show when={mode() === "wysiwyg"}>
          <style>{`#${uniqueId}{${props.styles}}`}</style>
        </Show>
        <EditorContent editor={editor()} class="grow px-3 pt-3" hidden={mode() !== "wysiwyg"} />
        <div class="grow bg-surface-extra-subtle" hidden={mode() !== "code"}>
          <CodeMirror
            value={
              codeOnly()
                ? props.code
                : prependStyles(props.styles, replacePillWithPlaceholder(editor().getHTML(), props.delimiter))
            }
            onChange={(value) =>
              untrack(() => {
                const parsed = props.parse(value);

                // Checked on every change, so removing the unsupported HTML brings back the WYSIWYG editor
                const isCodeOnly = requiresCodeMode(value);
                if (!isCodeOnly) {
                  // Before leaving code only, otherwise the code editor briefly shows TipTap's outdated content
                  const content = splitStyleAndContent(toTipTapContent(parsed, props.options, t));
                  editor()
                    .chain()
                    // TipTap's content from before was parsed lossy while code only, so it must not be restorable by undo
                    .setMeta("addToHistory", !codeOnly())
                    .setContent(content.html)
                    .run();
                  props.setStyles(content.styles);
                }
                props.setCodeOnly(isCodeOnly);

                // no need to add the styles because they are already included in the (parsed)value
                props.onChange(parsed);
              })
            }
            options={{ gutter: false }}
          />
        </div>
      </div>
    </div>
  );
};

const Toolbar = () => (
  <div class="p-1">
    <div class="flex gap-1">
      <FontFamilyCommand />
      <TextTypeCommand />
      <TextColorCommand />
      <TextBackgroundColorCommand />

      <CommandSeparator />

      <BoldCommand />
      <ItalicCommand />
      <UnderlineCommand />
      <StrikeThroughCommand />

      <CommandSeparator />

      <TextAlignLeftCommand />
      <TextAlignCenterCommand />
      <TextAlignRightCommand />
      <TextJustifyFullCommand />
    </div>

    <SeparatorLine class="my-1" />

    <div class="flex gap-1">
      <LineHeightCommand />

      <CommandSeparator />

      <OutdentCommand />
      <IndentCommand />

      <CommandSeparator />

      <BulletListCommand />
      <OrderedListCommand />

      <CommandSeparator />

      <TableCommand />

      <CommandSeparator />

      <LinkCommand />
      <ImageCommand />

      <HorizontalLineCommand />

      <CommandSeparator />

      <PlaceholderCommand />

      <CommandSeparator />

      <CodeCommand />
    </div>
  </div>
);

const CommandSeparator = () => <div class="my-2 border-l border-subtle" />;

const WysiwygWithErrorBoundary = withErrorBoundary(Wysiwyg);

export default WysiwygWithErrorBoundary;
